import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  List,
  ListItem,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CustomButton from "../UI/CustomButton";

// The fixed set of module types the backend recognizes (TrialModule.choices) — offered by
// the "Add Module" menu. Always all 6, always clickable: a module can repeat, so there's no
// "already present" filtering.
const MODULE_TYPES = [
  "Instructions",
  "Photos",
  "POEM",
  "Advanced Surveys",
  "Videos",
  "AI Assessment",
];

// Display-only labels for module types whose backend value (TrialModule.choices) is kept
// stable for matching/persistence — only what's shown to staff changes.
const MODULE_DISPLAY_LABELS = {
  POEM: "POEMs",
  "Advanced Surveys": "ePRO",
};
const moduleLabel = (module) => MODULE_DISPLAY_LABELS[module] || module;

const EXTRA_LANGUAGES = [
  { key: "de", label: "German (DE)" },
  { key: "es", label: "Spanish (ES)" },
];

let clientKeySeq = 0;
const nextClientKey = (prefix) => `${prefix}-${++clientKeySeq}`;

// Only rows the trial actually has — no synthesized defaults. A trial that's never touched
// this screen starts empty; presence in the list is what "enabled" used to mean.
const buildInitialModuleOrder = (trialDetails) => {
  if (!trialDetails?.trial_modules?.length) return [];

  return [...trialDetails.trial_modules]
    .sort((a, b) => a.position - b.position)
    .map((m) => ({
      clientKey: m.id != null ? `id-${m.id}` : nextClientKey("existing"),
      id: m.id,
      module: m.module,
      title: m.title || m.module,
      title_de: m.title_de || "",
      title_es: m.title_es || "",
      enabled_de: !!m.title_de,
      enabled_es: !!m.title_es,
      reward_points: m.reward_points || 0,
      is_required: m.is_required ?? false,
    }));
};

// Index of each row within the rows sharing its module (0 = the first occurrence) — used to
// distinguish testids and default titles across genuine repeats.
const withOccurrenceIndex = (moduleOrder) => {
  const seen = {};
  return moduleOrder.map((item) => {
    const occurrence = seen[item.module] || 0;
    seen[item.module] = occurrence + 1;
    return { ...item, occurrence };
  });
};

const slug = (module) => module.replace(/ /g, "-").toLowerCase();
const testIdBase = (item) =>
  item.occurrence > 0 ? `${slug(item.module)}-${item.occurrence + 1}` : slug(item.module);

const AppActivitiesOrder = ({
  trialDetails,
  setTrialDetails,
  hasBasicInfoChanged,
  setHasBasicInfoChanged,
  handleBasicInfoUpdate,
}) => {
  const isSessionBased = trialDetails?.trial_structure === "session_based";

  const [moduleOrder, setModuleOrder] = useState(() => buildInitialModuleOrder(trialDetails));
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [pendingDeleteIndex, setPendingDeleteIndex] = useState(null);

  // Re-initialize when trial structure changes
  useEffect(() => {
    setModuleOrder(buildInitialModuleOrder(trialDetails));
  }, [trialDetails?.trial_structure]);

  // Sync local moduleOrder back into trialDetails whenever it changes. `enabled` is
  // intentionally omitted — the backend defaults it to true, and presence in this list is
  // now the only "enabled" signal.
  useEffect(() => {
    setTrialDetails((prev) => ({
      ...prev,
      trial_modules: moduleOrder.map((item, index) => ({
        id: item.id,
        module: item.module,
        title: item.title,
        title_de: item.enabled_de ? item.title_de : "",
        title_es: item.enabled_es ? item.title_es : "",
        position: index + 1,
        reward_points: item.reward_points,
        is_required: item.is_required,
      })),
    }));
  }, [moduleOrder]);

  const moveItem = (index, direction) => {
    setModuleOrder((prev) => {
      const newOrder = [...prev];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= newOrder.length) return prev;
      const temp = newOrder[index];
      newOrder[index] = newOrder[targetIndex];
      newOrder[targetIndex] = temp;
      return newOrder;
    });
    setHasBasicInfoChanged(true);
  };

  const handleTitleChange = (index, newTitle) => {
    setModuleOrder((prev) => {
      const newOrder = [...prev];
      newOrder[index] = { ...newOrder[index], title: newTitle };
      return newOrder;
    });
    setHasBasicInfoChanged(true);
  };

  const handleRewardPointsChange = (index, value) => {
    setModuleOrder((prev) => {
      const newOrder = [...prev];
      newOrder[index] = { ...newOrder[index], reward_points: Math.max(0, parseInt(value) || 0) };
      return newOrder;
    });
    setHasBasicInfoChanged(true);
  };

  const handleLangToggle = (index, lang, enabled) => {
    setModuleOrder((prev) => {
      const newOrder = [...prev];
      newOrder[index] = {
        ...newOrder[index],
        [`enabled_${lang}`]: enabled,
        ...(!enabled && { [`title_${lang}`]: "" }),
      };
      return newOrder;
    });
    setHasBasicInfoChanged(true);
  };

  const handleRequiredToggle = (index, checked) => {
    setModuleOrder((prev) => {
      const newOrder = [...prev];
      newOrder[index] = { ...newOrder[index], is_required: checked };
      return newOrder;
    });
    setHasBasicInfoChanged(true);
  };

  const handleLangTitleChange = (index, lang, value) => {
    setModuleOrder((prev) => {
      const newOrder = [...prev];
      newOrder[index] = { ...newOrder[index], [`title_${lang}`]: value };
      return newOrder;
    });
    setHasBasicInfoChanged(true);
  };

  const handleAddModule = (module) => {
    setModuleOrder((prev) => {
      const existingCount = prev.filter((m) => m.module === module).length;
      const newRow = {
        clientKey: nextClientKey("new"),
        id: undefined,
        module,
        title:
          existingCount > 0 ? `${moduleLabel(module)} ${existingCount + 1}` : moduleLabel(module),
        title_de: "",
        title_es: "",
        enabled_de: false,
        enabled_es: false,
        reward_points: 0,
        is_required: false,
      };
      return [...prev, newRow];
    });
    setHasBasicInfoChanged(true);
    setAddMenuAnchor(null);
  };

  const handleDeleteStep = (index) => {
    setModuleOrder((prev) => prev.filter((_, i) => i !== index));
    setHasBasicInfoChanged(true);
    setPendingDeleteIndex(null);
  };

  const rows = withOccurrenceIndex(moduleOrder);

  const hasDuplicateTitle = (row, index) =>
    rows.some(
      (other, otherIndex) =>
        otherIndex !== index &&
        other.module === row.module &&
        other.title.trim() === row.title.trim()
    );

  const rowPendingDelete = pendingDeleteIndex != null ? rows[pendingDeleteIndex] : null;

  return (
    <Box sx={{ position: "relative", width: "100%", mb: 2 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Box sx={{ maxWidth: 480, flex: 1 }}>
          <Box sx={{ mb: 1 }}>
            <CustomButton
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={(e) => setAddMenuAnchor(e.currentTarget)}
              data-testid="app-activities-add-module-btn"
            >
              Add Module
            </CustomButton>
            <Menu
              anchorEl={addMenuAnchor}
              open={!!addMenuAnchor}
              onClose={() => setAddMenuAnchor(null)}
            >
              {MODULE_TYPES.map((module) => (
                <MenuItem
                  key={module}
                  onClick={() => handleAddModule(module)}
                  data-testid={`app-activities-add-module-${slug(module)}`}
                >
                  {moduleLabel(module)}
                </MenuItem>
              ))}
            </Menu>
          </Box>

          <List>
            {rows.map((item, index) => (
              <ListItem key={item.clientKey} disableGutters>
                <Paper
                  sx={{
                    p: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    gap: 1,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {moduleLabel(item.module)} — Order {index + 1}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={item.is_required}
                            onChange={(e) => handleRequiredToggle(index, e.target.checked)}
                            inputProps={{
                              "data-testid": `app-module-${testIdBase(item)}-required`,
                            }}
                          />
                        }
                        label={<Typography variant="caption">Required</Typography>}
                        labelPlacement="start"
                      />
                    </Box>
                    <TextField
                      size="small"
                      fullWidth
                      value={item.title}
                      onChange={(e) => handleTitleChange(index, e.target.value)}
                      placeholder={moduleLabel(item.module)}
                      error={hasDuplicateTitle(item, index)}
                      helperText={
                        hasDuplicateTitle(item, index)
                          ? "Give each repeated step its own title so patients can tell them apart"
                          : undefined
                      }
                      sx={{ mt: 0.5 }}
                    />
                    {isSessionBased && (
                      <TextField
                        size="small"
                        type="number"
                        fullWidth
                        label="Reward Points"
                        value={item.reward_points}
                        onChange={(e) => handleRewardPointsChange(index, e.target.value)}
                        placeholder="0"
                        inputProps={{
                          min: 0,
                          "data-testid": `app-module-${testIdBase(item)}-reward-points`,
                        }}
                        InputProps={{
                          endAdornment: <InputAdornment position="end">pts</InputAdornment>,
                        }}
                        sx={{ mt: 1 }}
                      />
                    )}
                    <Box sx={{ mt: 0.5 }}>
                      {EXTRA_LANGUAGES.map(({ key, label }) => (
                        <Box key={key}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={item[`enabled_${key}`]}
                                onChange={(e) => handleLangToggle(index, key, e.target.checked)}
                                inputProps={{
                                  "data-testid": `app-module-${testIdBase(item)}-${key}-checkbox`,
                                }}
                              />
                            }
                            label={<Typography variant="caption">{label}</Typography>}
                          />
                          {item[`enabled_${key}`] && (
                            <TextField
                              size="small"
                              fullWidth
                              value={item[`title_${key}`]}
                              onChange={(e) => handleLangTitleChange(index, key, e.target.value)}
                              placeholder={`${item.title} (${key.toUpperCase()})`}
                              inputProps={{
                                "data-testid": `app-module-${testIdBase(item)}-${key}-input`,
                              }}
                              sx={{ mb: 0.5 }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Tooltip title="Remove this step">
                      <IconButton
                        size="small"
                        onClick={() => setPendingDeleteIndex(index)}
                        data-testid={`app-module-${testIdBase(item)}-delete`}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <IconButton
                      size="small"
                      onClick={() => moveItem(index, -1)}
                      disabled={index === 0}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveItem(index, 1)}
                      disabled={index === rows.length - 1}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Paper>
              </ListItem>
            ))}
          </List>
        </Box>

        <CustomButton
          size="small"
          id="app-activities-settings"
          onClick={handleBasicInfoUpdate}
          disabled={!hasBasicInfoChanged}
        >
          Save
        </CustomButton>
      </Box>

      <Dialog open={!!rowPendingDelete} onClose={() => setPendingDeleteIndex(null)}>
        <DialogTitle>Remove {rowPendingDelete?.title || rowPendingDelete?.module}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This removes the &ldquo;{rowPendingDelete?.title || rowPendingDelete?.module}&rdquo;
            step from this trial&apos;s activity order. Any photo types, video types, surveys, or
            other items already configured for it on individual visits will be deleted too. This
            can&apos;t be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDeleteIndex(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => handleDeleteStep(pendingDeleteIndex)}
            data-testid="app-activities-confirm-delete-btn"
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppActivitiesOrder;
